/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { HTMLAttributes, ReactNode } from 'react';

import { ActionOverflow, Card, Table, Typography2 } from '@coursera/cds-core';
import type { TableProps } from '@coursera/cds-core';

import _t from 'i18n!nls/enterprise-ui';

const styles = {
  card: css`
    && {
      height: auto;
    }

    && .cds-Card-innerCard {
      padding: 0;
      gap: 0;
      height: auto;
      justify-content: flex-start;
    }
  `,
  header: css`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--cds-spacing-200);
    padding: var(--cds-spacing-300) var(--cds-spacing-300) 0;
    flex-shrink: 0;
  `,
  titleGroup: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
    min-width: 0;
  `,
  subtitle: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
  lastUpdated: css`
    padding: 0 var(--cds-spacing-300);
    margin-bottom: 0;
    margin-top: var(--cds-spacing-100);
  `,
  tableContainer: css`
    padding: var(--cds-spacing-200) 0 0;
    overflow: auto;

    figcaption {
      margin-bottom: 0 !important;
    }

    table {
      border-collapse: separate !important;
    }

    && .cds-Table-scrollWrapper {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
      border-left: none;
      border-right: none;
    }

    && .cds-Table-scrollWrapper + div {
      border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);
      border-left: none;
      border-right: none;
      border-bottom: none;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    thead th {
      position: sticky;
      top: 0;
      z-index: 3;
    }

    th,
    td {
      min-width: 180px;
    }
  `,
  emptyState: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: var(--cds-spacing-50);
    padding: var(--cds-spacing-400) var(--cds-spacing-300);
  `,
};

type RowKey<TRow extends Record<string, unknown>> = keyof TRow & string;

export type TableCardProps<TRow extends Record<string, unknown>> = Pick<
  TableProps<TRow, RowKey<TRow>>,
  | 'columns'
  | 'maxHeight'
  | 'defaultPageSize'
  | 'pageSizeInputOptions'
  | 'defaultSortKey'
  | 'defaultSortDirection'
  | 'status'
  | 'renderStatus'
> & {
  /** Data rows. Accepts readonly arrays (e.g. frozen Apollo results) — copied before the table sorts in place. */
  rows: readonly TRow[];
  /** Header title. When omitted, no heading is rendered. */
  title?: string;
  /** Secondary line rendered under the title. */
  subtitle?: string;
  /** When set, a "Last updated: …" line is shown above the table. */
  lastUpdated?: string | null;
  /**
   * Menu entries (e.g. `ActionOverflow.Item`s) shown in the header's option menu. Hidden when there are no rows.
   */
  menuItems?: ReactNode;
  'aria-label'?: string;
} & HTMLAttributes<HTMLDivElement>;

const DefaultEmptyState = () => (
  <div css={styles.emptyState} data-testid="table-card-empty">
    <Typography2 component="p" variant="subtitleMedium" color="supportText">
      {_t('No data found')}
    </Typography2>
    <Typography2 component="p" variant="bodySecondary" color="supportText">
      {_t('No data matches your selected filters.')}
    </Typography2>
  </div>
);

/**
 * A card that presents a data table with a header, an optional option menu, an
 * optional "last updated" line, and built-in empty-state handling.
 *
 * Wraps the CDS `Table` inside a `Card`: the card owns the layout (header +
 * option-menu shell + scrollable, sticky-header table) while consumers supply the
 * columns/rows and the menu's actions. The option menu's accessible label is
 * derived from `title`, and rows are copied before rendering so frozen Apollo
 * arrays can be sorted in place (`totalRows` is derived from `rows`).
 *
 * Empty data is handled one of two ways. Leave `status` unset and the card shows
 * its built-in empty state whenever `rows` is empty. Or set `status` to take over
 * the loading, error, and empty views yourself: the table always renders and CDS
 * displays the state returned by `renderStatus`.
 *
 * @example
 * ```tsx
 * <TableCard
 *   title="Learner activity"
 *   subtitle="Last 30 days"
 *   aria-label="Learner activity"
 *   lastUpdated={lastUpdated}
 *   columns={columns}
 *   rows={rows}
 *   defaultSortKey="name"
 *   defaultSortDirection="ascending"
 * />
 * ```
 */
function TableCard<TRow extends Record<string, unknown>>({
  columns,
  rows,
  title,
  subtitle,
  lastUpdated,
  menuItems,
  'aria-label': ariaLabel,
  maxHeight,
  defaultPageSize,
  pageSizeInputOptions,
  defaultSortKey,
  defaultSortDirection,
  status,
  renderStatus,
  ...rest
}: TableCardProps<TRow>) {
  const isEmpty = rows.length === 0;
  const showStandaloneEmpty = isEmpty && status === undefined;
  const showMenu = Boolean(menuItems) && !isEmpty;
  const showHeader = Boolean(title || subtitle || showMenu);
  const menuAriaLabel = title ? _t('#{title} actions', { title }) : _t('Table actions');

  return (
    <Card {...rest} css={styles.card} data-testid="table-card">
      {showHeader && (
        <div css={styles.header} data-testid="table-card-header">
          <div css={styles.titleGroup}>
            {title && (
              <Typography2 component="h3" variant="titleXSmall">
                {title}
              </Typography2>
            )}
            {subtitle && (
              <Typography2 component="p" variant="bodySecondary" css={styles.subtitle}>
                {subtitle}
              </Typography2>
            )}
          </div>
          {showMenu && (
            <ActionOverflow
              size="small"
              variant="ghostSecondary"
              data-testid="table-card-menu"
              aria-label={menuAriaLabel}
            >
              {menuItems}
            </ActionOverflow>
          )}
        </div>
      )}

      {lastUpdated && (
        <Typography2
          component="p"
          variant="bodyTertiary"
          color="supportText"
          css={styles.lastUpdated}
          data-testid="table-card-last-updated"
        >
          {_t('Last updated: #{lastUpdated}', { lastUpdated })}
        </Typography2>
      )}

      {showStandaloneEmpty ? (
        <DefaultEmptyState />
      ) : (
        <div css={styles.tableContainer}>
          <Table<TRow, RowKey<TRow>>
            columns={columns}
            rows={[...rows]}
            totalRows={rows.length}
            aria-label={ariaLabel ?? title}
            maxHeight={maxHeight}
            defaultPageSize={defaultPageSize}
            pageSizeInputOptions={pageSizeInputOptions}
            defaultSortKey={defaultSortKey}
            defaultSortDirection={defaultSortDirection}
            status={status}
            renderStatus={renderStatus}
          />
        </div>
      )}
    </Card>
  );
}

export { TableCard };
export default TableCard;
